import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { paths } from "../routes/paths";
import { getAppData } from "../services/authServices";
import {
  ACCESS_DENIED_MESSAGE,
  AUTH_STORAGE_KEYS,
  extractUserId,
  isAuthorizedUserId,
} from "../utils/auth";

interface AuthState {
  companyId: number | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  profile: Record<string, unknown> | null;
  roleTitle: string | null;
  token: string | null;
  user: Record<string, unknown> | null;
  userId: number | null;
}

interface LoginPayload {
  companyId: number;
  token: string;
  user: Record<string, unknown>;
}

interface AuthContextValue extends AuthState {
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => void;
}

const initialState: AuthState = {
  companyId: null,
  isAuthenticated: false,
  isLoading: true,
  profile: null,
  roleTitle: null,
  token: null,
  user: null,
  userId: null,
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function clearAuthStorage() {
  if (typeof window === "undefined") {
    return;
  }

  Object.values(AUTH_STORAGE_KEYS).forEach((key) => window.localStorage.removeItem(key));
}

function readJsonObject(key: string): Record<string, unknown> | null {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(key);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  const syncFromStorage = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const token = window.localStorage.getItem(AUTH_STORAGE_KEYS.token);
    const companyIdRaw = window.localStorage.getItem(AUTH_STORAGE_KEYS.companyId);
    const roleTitle = window.localStorage.getItem(AUTH_STORAGE_KEYS.roleTitle);
    const user = readJsonObject(AUTH_STORAGE_KEYS.user);
    const profile = readJsonObject(AUTH_STORAGE_KEYS.profile);

    const companyId = companyIdRaw ? Number(companyIdRaw) : NaN;
    const userId = extractUserId(profile);

    if (!token || !Number.isFinite(companyId) || !user || !profile || !isAuthorizedUserId(userId)) {
      clearAuthStorage();
      setState({
        ...initialState,
        isLoading: false,
      });
      return;
    }

    setState({
      companyId,
      isAuthenticated: true,
      isLoading: false,
      profile,
      roleTitle,
      token,
      user,
      userId,
    });
  }, []);

  useEffect(() => {
    syncFromStorage();
  }, [syncFromStorage]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (
        event.key == null ||
        Object.values(AUTH_STORAGE_KEYS).includes(
          event.key as (typeof AUTH_STORAGE_KEYS)[keyof typeof AUTH_STORAGE_KEYS],
        )
      ) {
        syncFromStorage();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [syncFromStorage]);

  const login = useCallback(async ({ companyId, token, user }: LoginPayload) => {
    const appData = await getAppData(token, companyId);
    const profile = (appData.current_user as Record<string, unknown> | undefined) || null;
    const roleTitle = typeof appData.role_title === "string" ? appData.role_title : null;
    const userId = extractUserId(profile);

    if (!profile || !isAuthorizedUserId(userId)) {
      clearAuthStorage();
      setState({
        ...initialState,
        isLoading: false,
      });
      throw new Error(ACCESS_DENIED_MESSAGE);
    }

    window.localStorage.setItem(AUTH_STORAGE_KEYS.token, token);
    window.localStorage.setItem(AUTH_STORAGE_KEYS.companyId, String(companyId));
    window.localStorage.setItem(AUTH_STORAGE_KEYS.user, JSON.stringify(user));
    window.localStorage.setItem(AUTH_STORAGE_KEYS.profile, JSON.stringify(profile));
    if (roleTitle) {
      window.localStorage.setItem(AUTH_STORAGE_KEYS.roleTitle, roleTitle);
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEYS.roleTitle);
    }

    setState({
      companyId,
      isAuthenticated: true,
      isLoading: false,
      profile,
      roleTitle,
      token,
      user,
      userId,
    });
  }, []);

  const logout = useCallback(() => {
    clearAuthStorage();
    setState({
      ...initialState,
      isLoading: false,
    });

    if (typeof window !== "undefined" && window.location.pathname !== paths.login) {
      window.location.assign(paths.login);
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      logout,
    }),
    [login, logout, state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
